import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Brain, Copy, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminBrainKeys() {
  const [keys, setKeys] = useState([]);
  const [app, setApp] = useState("");
  const [fresh, setFresh] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/brain-keys").then((r) => setKeys(r.data.keys)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/brain-keys", { app }); setFresh(data); setApp(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create key"); }
    setBusy(false);
  };
  const revoke = async (id) => { await api.delete(`/admin/brain-keys/${id}`).catch(() => {}); load(); };
  const copy = (t) => navigator.clipboard.writeText(t).then(() => toast.success("Copied")).catch(() => toast.error("Copy failed"));

  return (
    <div className="mt-14 pt-10 border-t border-border" data-testid="admin-brain">
      <p className="text-xs uppercase tracking-[0.2em] text-primary mb-2">Sister apps</p>
      <h2 className="font-serif text-3xl sm:text-4xl mb-2 flex items-center gap-2"><Brain className="w-6 h-6 text-primary" /> Coach Brain connection</h2>
      <p className="text-muted-foreground mb-6 max-w-xl">Give Soulumo, Absorbo or Reclaim a private key so they can talk to Xcelio's coach directly, with the same personality, Knowledge Base and safety. Update the Knowledge Base here and every app gets it instantly.</p>

      <div className="max-w-xl flex flex-col sm:flex-row gap-2 mb-4">
        <Input value={app} onChange={(e) => setApp(e.target.value)} placeholder="App name, e.g. Soulumo" data-testid="brain-app-input" />
        <Button onClick={create} disabled={busy || !app.trim()} className="rounded-full" data-testid="brain-create-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create key"}
        </Button>
      </div>

      {fresh && (
        <div className="max-w-2xl rounded-2xl border border-amber-400/40 bg-amber-400/[0.06] p-5 mb-5" data-testid="brain-new-key">
          <p className="text-sm mb-2">Key for <b>{fresh.app}</b>. Copy it now, because it won't be shown again.</p>
          <div className="flex gap-2 items-center">
            <code className="flex-1 min-w-0 truncate text-xs bg-background/60 rounded-lg px-3 py-2" data-testid="brain-new-key-value">{fresh.key}</code>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => copy(fresh.key)} data-testid="brain-copy-key"><Copy className="w-3.5 h-3.5" /></Button>
          </div>
          <p className="text-xs text-muted-foreground mt-3 break-all">
            Ask: <code>POST {BACKEND_URL}/api/brain/ask</code> · header <code>Authorization: Bearer &lt;key&gt;</code> · body <code>{`{"message": "...", "member_id": "their-user-id", "member_name": "James"}`}</code><br />
            Knowledge sync: <code>GET {BACKEND_URL}/api/brain/knowledge</code>
          </p>
        </div>
      )}

      <div className="max-w-2xl space-y-2">
        {keys.length === 0 && <p className="text-sm text-muted-foreground" data-testid="brain-empty">No sister apps connected yet.</p>}
        {keys.map((k) => (
          <div key={k.id} className="flex items-center gap-3 rounded-xl border border-border p-3 text-sm" data-testid={`brain-key-${k.id}`}>
            <span className="flex-1 min-w-0"><b>{k.app}</b> <span className="text-muted-foreground text-xs">· {k.prefix}… · {k.calls} calls · this month £{(k.month_spend_gbp ?? 0).toFixed(2)} of £10{k.last_used_at ? ` · last used ${k.last_used_at.slice(0, 10)}` : ""}</span></span>
            <button onClick={() => revoke(k.id)} className="text-muted-foreground hover:text-destructive" aria-label="Revoke key" data-testid={`brain-revoke-${k.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
